import { useMemo, useState } from "react";
import {
	CHART_INK,
	colorForSeries,
	formatCompactNumber,
} from "../../lib/palette";
import type { PivotedChart } from "../../lib/pivot";

interface BarChartProps {
	chart: PivotedChart;
	height?: number;
}

const MAX_BAR_WIDTH = 24;
const BAR_GAP = 2;
const PADDING = { top: 16, right: 12, bottom: 28, left: 44 };

export function BarChart({ chart, height = 220 }: BarChartProps) {
	const [hover, setHover] = useState<{
		category: string;
		series: string;
		value: number;
		x: number;
		y: number;
	} | null>(null);

	const width = 640;
	const { categories, series } = chart;

	const maxValue = useMemo(() => {
		let max = 0;
		for (const s of series) for (const p of s.points) max = Math.max(max, p.y);
		return max === 0 ? 1 : max;
	}, [series]);

	if (categories.length === 0 || series.length === 0) {
		return (
			<div className="text-xs font-mono text-base-content/50 py-6 text-center">
				No data for this selection.
			</div>
		);
	}

	const plotWidth = width - PADDING.left - PADDING.right;
	const plotHeight = height - PADDING.top - PADDING.bottom;
	const groupWidth = plotWidth / categories.length;
	const barWidth = Math.min(
		MAX_BAR_WIDTH,
		(groupWidth - BAR_GAP * (series.length + 1)) / series.length,
	);

	const yTicks = 4;
	const tickValues = Array.from(
		{ length: yTicks + 1 },
		(_, i) => (maxValue / yTicks) * i,
	);

	return (
		<div className="relative">
			{series.length > 1 && (
				<div className="flex flex-wrap gap-3 mb-2 px-1">
					{series.map((s) => (
						<div
							key={s.name}
							className="flex items-center gap-1.5 text-[10px] font-mono text-base-content/70"
						>
							<span
								className="inline-block w-2.5 h-2.5 rounded-sm"
								style={{
									backgroundColor: colorForSeries(s.name, series.indexOf(s)),
								}}
							/>
							{s.name}
						</div>
					))}
				</div>
			)}
			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="w-full h-auto"
				style={{ maxHeight: height }}
			>
				<title>
					Bar chart of {series.map((s) => s.name).join(", ")} by category
				</title>
				{tickValues.map((value) => {
					const y = PADDING.top + plotHeight - (value / maxValue) * plotHeight;
					return (
						<g key={value}>
							<line
								x1={PADDING.left}
								x2={width - PADDING.right}
								y1={y}
								y2={y}
								stroke={CHART_INK.gridline}
								strokeWidth={1}
							/>
							<text
								x={PADDING.left - 8}
								y={y + 3}
								textAnchor="end"
								fontSize={9}
								fill={CHART_INK.muted}
								fontFamily="monospace"
							>
								{formatCompactNumber(value)}
							</text>
						</g>
					);
				})}
				<line
					x1={PADDING.left}
					x2={width - PADDING.right}
					y1={PADDING.top + plotHeight}
					y2={PADDING.top + plotHeight}
					stroke={CHART_INK.baseline}
					strokeWidth={1}
				/>

				{categories.map((category, ci) => {
					const groupX = PADDING.left + ci * groupWidth;
					return (
						<g key={category}>
							{series.map((s, si) => {
								const value = s.points[ci]?.y ?? 0;
								const barHeight = (value / maxValue) * plotHeight;
								const x =
									groupX +
									BAR_GAP +
									si * (barWidth + BAR_GAP) +
									(groupWidth - series.length * (barWidth + BAR_GAP)) / 2;
								const y = PADDING.top + plotHeight - barHeight;
								const isHovered =
									hover?.category === category && hover.series === s.name;
								return (
									<rect
										key={s.name}
										x={x}
										y={y}
										width={Math.max(0, barWidth)}
										height={Math.max(0, barHeight)}
										rx={4}
										fill={colorForSeries(s.name, si)}
										opacity={isHovered ? 1 : 0.85}
										onPointerMove={(e) => {
											const rect =
												e.currentTarget.ownerSVGElement?.getBoundingClientRect();
											setHover({
												category,
												series: s.name,
												value,
												x: rect ? e.clientX - rect.left : 0,
												y: rect ? e.clientY - rect.top : 0,
											});
										}}
										onPointerLeave={() => setHover(null)}
									/>
								);
							})}
							<text
								x={groupX + groupWidth / 2}
								y={height - PADDING.bottom + 16}
								textAnchor="middle"
								fontSize={9}
								fill={CHART_INK.muted}
								fontFamily="monospace"
							>
								{category.length > 8 ? `${category.slice(0, 7)}…` : category}
							</text>
						</g>
					);
				})}
			</svg>
			{hover && (
				<div
					className="absolute pointer-events-none bg-base-100 border border-base-content/10 rounded px-2 py-1 text-[10px] font-mono shadow-lg z-20"
					style={{
						left: Math.min(hover.x + 8, width - 120),
						top: Math.max(hover.y - 36, 0),
					}}
				>
					<div className="text-base-content/60">
						{hover.category} · {hover.series}
					</div>
					<div className="text-base-content font-semibold">
						{formatCompactNumber(hover.value)}
					</div>
				</div>
			)}
		</div>
	);
}
